import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ButtonColor } from 'common';
import { DashboardCustomDateDialogData, DashboardCustomDateDialogResult } from './dashboard-custom-date-dialog.model';
import { IsoDate } from '../../domain/model/dashboard-filters.model';
import { fromIsoDate, isValidCustomRange, toIsoDate } from '../dashboard-filters.helper';

/**
 * Picks the dashboard's custom date range. Closes with the chosen range, or with nothing on cancel,
 * on the backdrop and on Escape — the caller commits, so a dismissed dialog changes no filter.
 *
 * Hand-rolled rather than composed from `ifc-base-dialog`: `BaseDialogActionData` carries no `disabled`
 * and the shell's action button binds none, so the shared footer cannot gate SUBMIT on a valid range.
 */
@UntilDestroy()
@Component({
  selector: 'ifbp-dashboard-custom-date-dialog',
  templateUrl: './dashboard-custom-date-dialog.component.html',
  styleUrls: ['./dashboard-custom-date-dialog.component.scss'],
  standalone: false,
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }],
})
export class DashboardCustomDateDialogComponent implements OnInit {
  readonly ButtonColor = ButtonColor;
  readonly form: UntypedFormGroup;
  readonly fromControl: UntypedFormControl;
  readonly toControl: UntypedFormControl;
  /** Upper bound for both pickers (company calendar day, local midnight). */
  readonly todayDate: Date;
  /** Lower bound for the "to" picker: the start date currently in the form (`null` while empty). */
  toMinDate: Date | null = null;
  submitDisabled = true;

  constructor(
    public dialogRef: MatDialogRef<DashboardCustomDateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DashboardCustomDateDialogData,
    private readonly fb: UntypedFormBuilder,
  ) {
    this.form = this.fb.group({ from: [null], to: [null] });
    this.fromControl = this.form.get('from') as UntypedFormControl;
    this.toControl = this.form.get('to') as UntypedFormControl;
    this.todayDate = fromIsoDate(data.today);
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.refreshBounds());

    // `ifc-datepicker` only shows an initial value set a turn after it is created.
    setTimeout(() => {
      this.form.patchValue({
        from: this.data.from ? fromIsoDate(this.data.from) : null,
        to: this.data.to ? fromIsoDate(this.data.to) : null,
      });
    }, 0);
  }

  static show(
    dialog: MatDialog,
    data: DashboardCustomDateDialogData,
  ): Observable<DashboardCustomDateDialogResult | undefined> {
    return dialog
      .open<DashboardCustomDateDialogComponent, DashboardCustomDateDialogData, DashboardCustomDateDialogResult>(
        DashboardCustomDateDialogComponent,
        {
          data,
          closeOnNavigation: true,
          width: '580px',
          autoFocus: false,
        },
      )
      .afterClosed();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    const [from, to] = this.isoPair();
    if (!isValidCustomRange(from, to, this.data.today)) {
      return;
    }
    this.dialogRef.close({ from, to });
  }

  private isoPair(): [IsoDate | null, IsoDate | null] {
    const value = this.form.value as { from: unknown; to: unknown };
    return [toIsoDate(value.from), toIsoDate(value.to)];
  }

  private refreshBounds(): void {
    const [from, to] = this.isoPair();
    this.toMinDate = from ? fromIsoDate(from) : null;
    this.submitDisabled = !isValidCustomRange(from, to, this.data.today);
  }
}
